<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.progress_evaluation'),
                        href: meta.links.index,
                    },
                    {
                        name: progressEvaluation.category_name,
                        href: meta.links.category_index,
                    },
                    {
                        name: group.name,
                        href: meta.links.progress_evaluation_group_index,
                    },
                ]"
            />
        </template>

        <div class="w-full flex justify-end" v-if="meta.can.edit">
            <AppButton :href="meta.links.edit">
                {{ $t('messages.edit') }}
            </AppButton>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-1">
                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('messages.information') }}</h3>
                <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                    <div class="sm:col-span-2">
                        <label for="date" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.date') }}
                        </label>
                        <div class="mt-1">
                            {{ progressEvaluationGroup.date }}
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label for="max_score" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.max_score') }}
                        </label>
                        <div class="mt-1">
                            {{ progressEvaluationGroup.max_score }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.score') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    d10
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.absent') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.comment') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="hover:bg-gray-200" v-for="score in scores" :key="score.id">
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ score.student.full_name }}
                                </td>
                                <td
                                    class="px-4 sm:px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        {{ score.score }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ score.decimal_score }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <input
                                        disabled
                                        type="checkbox"
                                        :checked="score.absent"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ score.comment }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="mt-5">
            {{ $t('messages.summary') }}
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    &nbsp;
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ progressEvaluationGroup.date }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="hover:bg-gray-200" v-for="item in summaryItems">
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ item.title }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ item.first }}

                                    <span class="text-gray-500" v-if="item.secondary">
                                        {{ item.secondary }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';

export default {
    components: {
        AppButton,
        PanelPage,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'group', 'progressEvaluation', 'progressEvaluationGroup', 'scores', 'summaryItems'],
};
</script>
